import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  ParagraphText,
  UtilityText,
  Checkbox,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';
import type { GDSStyleObject } from '@leagueplatform/genesis-core';
import type {
  DataTableRow,
  MultiEnvironmentDataTableRow,
  SingleEnvironmentDataTableRow,
} from '@web-config-app/core-react';
import type { SyncModeConfig } from '../entity-data-table-modal/sync-mode.types';
import { DiffCell } from './diff-cell.component';

/**
 * Type guard to check if a table row is a multi-environment row.
 * Multi-environment rows have both `data` and `otherData` properties.
 */
export const isMultiEnvironmentRow = (
  row: SingleEnvironmentDataTableRow | MultiEnvironmentDataTableRow,
): row is MultiEnvironmentDataTableRow => 'otherData' in row;

const baseCellStyles: GDSStyleObject = {
  padding: '$half',
  borderStyle: 'solid',
  borderColor: '$onSurfaceBorderSubdued',
  borderWidth: '$thin',
  verticalAlign: 'top',
};

const DataCell = ({ children }: React.PropsWithChildren<{}>) => (
  <Box
    as="td"
    css={{
      ...baseCellStyles,
      maxWidth: 550,
      wordWrap: 'break-word',
      width: 'auto',
    }}
  >
    <UtilityText as="div">{children}</UtilityText>
  </Box>
);

const TableHeaderCell = ({
  children,
  scope,
}: React.PropsWithChildren<{ scope: 'row' | 'col' }>) => (
  <Box
    as="th"
    scope={scope}
    css={
      scope === 'col'
        ? {
            ...baseCellStyles,
            backgroundColor: '$surfaceBackgroundTertiary',
            textAlign: 'start',
            width: scope === 'col' ? 'auto' : '30%',
          }
        : {
            ...baseCellStyles,
            textAlign: 'start',
            backgroundColor: '$surfaceBackgroundSecondary',
            width: '30%',
          }
    }
  >
    <UtilityText emphasis="emphasized">{children}</UtilityText>
  </Box>
);

export interface EntityDataTableProps {
  rows: Array<DataTableRow>;
  columnHeaders: Array<{ key: string; displayLabel: string }>;
  isMultiEnvironment: boolean;
  envDataDifferenceCount: number;
  /**
   * Optional sync mode configuration passed if table is used for multi-environment syncing.
   */
  syncMode?: Pick<SyncModeConfig, 'sourceEnvironment' | 'targetEnvironment'>;
}

export const EntityDataTable = ({
  rows,
  columnHeaders,
  isMultiEnvironment,
  syncMode,
  envDataDifferenceCount,
}: EntityDataTableProps) => {
  const { formatMessage } = useIntl();
  // if syncMode is set, show only differences by default
  const [onlyShowDifferences, setOnlyShowDifferences] = React.useState(
    !!syncMode,
  );

  React.useEffect(() => {
    setOnlyShowDifferences(!!syncMode);
  }, [syncMode]);

  const displayedRows = React.useMemo(() => {
    // Only shows the rows that represent a data difference between environments for the property path
    if (onlyShowDifferences && isMultiEnvironment) {
      const multiEnvRows = rows.filter(isMultiEnvironmentRow);
      return multiEnvRows.filter((row) => row.data !== row.otherData);
    }
    return rows;
  }, [rows, isMultiEnvironment, onlyShowDifferences]);

  /**
   * In sync mode, replace the default column headers with explicit
   * source/target labels so authors can tell at a glance which column
   * will be kept and which will be overwritten.
   */
  const resolvedColumnHeaders = React.useMemo(() => {
    if (!syncMode) return columnHeaders;

    return columnHeaders.map((header) => {
      if (header.key === 'data') {
        return {
          ...header,
          displayLabel: formatMessage(
            { id: 'SYNC_SOURCE_COLUMN_LABEL' },
            { environmentName: syncMode.sourceEnvironment.name },
          ),
        };
      }

      if (header.key === 'otherData') {
        return {
          ...header,
          displayLabel: formatMessage(
            { id: 'SYNC_TARGET_COLUMN_LABEL' },
            { environmentName: syncMode.targetEnvironment.name },
          ),
        };
      }

      return header;
    });
  }, [columnHeaders, syncMode, formatMessage]);

  return (
    <>
      <Box css={{ marginBlock: '$one' }}>
        {isMultiEnvironment && (
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '$one',
            }}
          >
            {/* aria-hidden because it is the caption of the table and available to screen readers below */}
            <ParagraphText css={{ margin: 0 }} aria-hidden="true">
              {envDataDifferenceCount > 0
                ? formatMessage(
                    { id: 'DIFFERENCES_FOUND' },
                    { count: envDataDifferenceCount },
                  )
                : formatMessage({ id: 'NO_DIFFERENCES_FOUND' })}
            </ParagraphText>
            <Checkbox
              aria-controls="data-table"
              id="only-show-differences-checkbox"
              name="only-show-differences-checkbox"
              label={
                <UtilityText>
                  {formatMessage({ id: 'SHOW_ONLY_DATA_DIFFERENCES' })}
                </UtilityText>
              }
              checked={onlyShowDifferences}
              onChange={(e) => setOnlyShowDifferences(e.target.checked)}
            />
          </Box>
        )}
      </Box>
      <Box
        as="table"
        id="data-table"
        aria-live="polite"
        css={{
          borderRadius: '$small',
          borderColor: '$onSurfaceBorderSubdued',
          borderWidth: '$thin',
          borderStyle: 'solid',
          borderSpacing: 0,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <VisuallyHidden as="caption">
          {envDataDifferenceCount > 0
            ? formatMessage(
                { id: 'TABLE_CAPTION_DIFFERENCES_FOUND' },
                { count: envDataDifferenceCount },
              )
            : formatMessage({ id: 'TABLE_CAPTION_NO_DIFFERENCES_FOUND' })}
        </VisuallyHidden>
        <Box as="thead">
          <Box as="tr" css={{ position: 'sticky', insetBlockStart: 0 }}>
            {resolvedColumnHeaders.map(({ key, displayLabel }) => (
              <TableHeaderCell key={key} scope="col">
                {displayLabel}
              </TableHeaderCell>
            ))}
          </Box>
        </Box>
        <Box as="tbody">
          {displayedRows.length > 0 &&
            displayedRows.map((row) =>
              isMultiEnvironment && isMultiEnvironmentRow(row) ? (
                <Box
                  as="tr"
                  key={row.path}
                  className={
                    row.data !== row.otherData ? 'highlight-unequal' : ''
                  }
                  css={{
                    '&.highlight-unequal td': {
                      backgroundColor: '$highlightBackgroundSubdued',
                      fontWeight: 'bold',
                    },
                  }}
                >
                  <TableHeaderCell scope="row">
                    {row.label ?? row.path}
                  </TableHeaderCell>
                  {/* current environment data cell */}
                  <DataCell>{row.data}</DataCell>
                  {/* other environment data cell */}
                  <DataCell>
                    {Boolean(syncMode) && row.data !== row.otherData ? (
                      <DiffCell oldValue={row.otherData} newValue={row.data} />
                    ) : (
                      row.otherData
                    )}
                  </DataCell>
                </Box>
              ) : (
                <Box as="tr" key={row.path}>
                  <TableHeaderCell scope="row">
                    {row.label ?? row.path}
                  </TableHeaderCell>
                  <DataCell>{row.data}</DataCell>
                </Box>
              ),
            )}
          {displayedRows.length === 0 && (
            <Box as="tr">
              <Box as="td" colSpan={columnHeaders.length}>
                <UtilityText emphasis="subtle" css={{ padding: '$half' }}>
                  {isMultiEnvironment
                    ? formatMessage({ id: 'NO_DIFFERENCES_FOUND' })
                    : formatMessage({ id: 'NO_DATA' })}
                </UtilityText>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
